import { DOCUMENT, isPlatformServer } from "@angular/common";
import { Inject, Injectable, PLATFORM_ID, REQUEST } from "@angular/core";
import { Meta, MetaDefinition } from "@angular/platform-browser";

@Injectable({ providedIn: "root" })
/**
 * Servicio para gestionar la etiqueta `<meta name="canonical">`.
 * Permite establecer o actualizar la URL canónica de una página.
 */
export class CanonicalService {
  constructor(
    @Inject(PLATFORM_ID) private readonly platformId,
    @Inject(REQUEST) private readonly request: Request,
    private readonly meta: Meta,
    @Inject(DOCUMENT) private readonly document: any,
  ) {}

  /**
   * Establece la URL canónica para el documento actual en un entorno del lado del servidor.
   *
   * Este método verifica si el código se está ejecutando en el servidor y si la solicitud HTTP está disponible.
   * Luego, construye una URL canónica basada en el host y la ruta del documento, forzando el uso de HTTPS.
   * Finalmente, crea un elemento `<link>` con el atributo `rel="canonical"` y lo agrega al `<head>` del documento.
   *
   * @remarks
   * Este método está diseñado para ser utilizado en aplicaciones Angular que se ejecutan en entornos de renderizado
   * del lado del servidor (Server-Side Rendering - SSR).
   *
   * @throws
   * Puede lanzar errores si `this.request` o `this.document` no están correctamente configurados.
   */
  setCanonicalUrl() {
    if (isPlatformServer(this.platformId) && this.request) {
      const host = this.document?.location?.host;
      const pathname = this.document?.location?.pathname;
      const url = `https://${host}${pathname}`; // <-- fuerza HTTPS
      const link: HTMLLinkElement = this.request?.["document"]?.createElement("link");
      link?.setAttribute("rel", "canonical");
      link?.setAttribute("href", url);
      this.request?.["document"]?.head?.appendChild(link);
    }
  }

  /**
   * Actualiza las etiquetas meta canónicas en el documento actual.
   *
   * Este método toma un array de definiciones de meta etiquetas y actualiza cada una de ellas en el documento.
   * Si la etiqueta es de tipo fecha, se normaliza a formato ISO antes de actualizarla.
   *
   * @param metaTagsArray - Array de definiciones de meta etiquetas a actualizar.
   */
  updateCanonicalUrls(metaTagsArray: MetaDefinition[]): void {
    metaTagsArray.forEach((metaTag) => {
      // Normaliza la fecha solo si el tag es de tipo fecha
      if ((metaTag.name?.toLowerCase().includes("date") || metaTag.property?.toLowerCase().includes("date")) && metaTag.content) {
        const raw: any = metaTag.content; // string | Date
        let date: Date;

        if (raw instanceof Date) {
          date = raw;
        } else {
          // corrige formato "YYYY-MM-DD HH:mm:ss" -> ISO
          const iso = typeof raw === "string" ? raw.replace(" ", "T").replace(/(?<!Z)$/, "Z") : String(raw);
          date = new Date(iso);
        }

        if (!isNaN(date.getTime())) {
          metaTag.content = date.toISOString();
        } else {
          return; // fecha inválida -> no se crea el tag
        }
      }

      // Actualiza las metatags
      if (metaTag.name) {
        this.meta.updateTag({ name: metaTag.name, content: metaTag.content });
      } else if (metaTag.property) {
        this.meta.updateTag({ property: metaTag.property, content: metaTag.content });
      }
    });
  }
}
